import React, { useEffect, useState } from 'react';
import { uploadFile } from '../../api';
import InlineLoading from '../InlineLoading';
import {
  getProviderApiErrorMessage,
  getProviderPaymentMethodLabel,
  PROVIDER_PAYMENT_METHODS,
  PROVIDER_TEXT_MAX_LENGTH,
  validateProviderPaymentForm,
} from '../../utils/provider';

const getDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const createInitialForm = (payment) => ({
  amount: payment?.amount ? String(payment.amount) : '',
  method: payment?.method || '',
  reference: payment?.reference || '',
  paidAt: getDateInputValue(payment?.paidAt),
  note: payment?.note || '',
  voucherFile: null,
  removeVoucher: false,
});

const ProviderPaymentModal = ({ payment, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState(createInitialForm(payment));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(createInitialForm(payment));
    setError('');
    setSubmitting(false);
  }, [isOpen, payment]);

  if (!isOpen || !payment) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'voucherFile' && value ? { removeVoucher: false } : {}),
    }));
  };

  const handleToggleVoucherRemoval = () => {
    setForm((prev) => ({
      ...prev,
      removeVoucher: !prev.removeVoucher,
      voucherFile: null,
    }));
  };

  const handleSubmit = async () => {
    setError('');
    const { amount, error: validationError } = validateProviderPaymentForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        amount,
        method: form.method,
        reference: form.reference,
        paidAt: form.paidAt || undefined,
        note: form.note,
      };

      if (form.voucherFile) {
        const response = await uploadFile(form.voucherFile);
        if (!response?.url) {
          throw new Error('No se pudo obtener la URL del comprobante.');
        }
        payload.voucherUrl = response.url;
      } else if (form.removeVoucher) {
        payload.voucherUrl = null;
      }

      await onSave(payload);
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al actualizar el pago.'));
    } finally {
      setSubmitting(false);
    }
  };

  const hasCurrentVoucher = Boolean(payment.voucherUrl) && !form.removeVoucher && !form.voucherFile;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Pago proveedor #{payment.id}
            </p>
            <h2 className="text-xl font-semibold text-slate-900">Editar pago</h2>
            <p className="text-sm text-slate-600">
              Las asignaciones se recalcularan al guardar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white p-2 text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Monto actual</p>
              <p className="text-lg font-semibold text-slate-900">
                ${Number(payment.amount || 0).toLocaleString('de-DE')}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Metodo actual</p>
              <p className="text-lg font-semibold text-slate-900">
                {getProviderPaymentMethodLabel(payment.method)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Fecha actual</p>
              <p className="text-lg font-semibold text-slate-900">
                {payment.paidAt ? new Date(payment.paidAt).toLocaleDateString('es-CL') : '—'}
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Monto</span>
              <input
                type="number"
                min="1"
                step="1"
                value={form.amount}
                onChange={(event) => handleChange('amount', event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Ej: 25000"
              />
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Metodo de pago</span>
              <select
                value={form.method}
                onChange={(event) => handleChange('method', event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              >
                <option value="">Selecciona un metodo</option>
                {PROVIDER_PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {getProviderPaymentMethodLabel(method)}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Referencia</span>
              <input
                type="text"
                value={form.reference}
                onChange={(event) => handleChange('reference', event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                maxLength={PROVIDER_TEXT_MAX_LENGTH}
                placeholder="Transferencia, cheque, etc."
              />
            </label>
            <label className="space-y-1 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Fecha de pago</span>
              <input
                type="date"
                value={form.paidAt}
                onChange={(event) => handleChange('paidAt', event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </label>
          </div>

          <label className="space-y-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">Nota</span>
            <textarea
              rows={3}
              value={form.note}
              onChange={(event) => handleChange('note', event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              maxLength={PROVIDER_TEXT_MAX_LENGTH}
              placeholder="Observaciones del pago."
            />
          </label>

          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-slate-700">Comprobante</p>
                <p className="text-xs text-slate-500">
                  Puedes reemplazar el archivo actual o eliminarlo.
                </p>
              </div>
              {payment.voucherUrl && (
                <button
                  type="button"
                  onClick={handleToggleVoucherRemoval}
                  className={`rounded px-3 py-2 text-sm ${
                    form.removeVoucher
                      ? 'bg-slate-800 text-white hover:bg-slate-700'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {form.removeVoucher ? 'Mantener comprobante' : 'Quitar comprobante'}
                </button>
              )}
            </div>

            {hasCurrentVoucher && (
              <a
                href={payment.voucherUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 underline"
              >
                Ver comprobante actual
              </a>
            )}

            {form.removeVoucher && (
              <p className="text-sm text-amber-700">
                El comprobante actual se eliminara al guardar.
              </p>
            )}

            <div className="space-y-1">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(event) => handleChange('voucherFile', event.target.files?.[0] || null)}
                className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm"
              />
              {form.voucherFile && (
                <p className="text-xs text-slate-500">{form.voucherFile.name}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? <InlineLoading label="Guardando" /> : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderPaymentModal;
